import React from "react";
import styles from "./Pagination.module.css";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const renderPageNumbers = () => {
    const pages: React.ReactNode[] = [];

    // Функция для добавления эллипсиса
    const addEllipsis = (key: string) => {
      pages.push(
        <span key={key} className={styles.ellipsis}>
          ...
        </span>
      );
    };

    // Функция для добавления номера страницы
    const addPageNumber = (pageNum: number) => {
      pages.push(
        <button
          key={pageNum}
          onClick={() => onPageChange(pageNum)}
          className={`${styles.pageButton} ${
            currentPage === pageNum ? styles.active : ""
          }`}
          aria-current={currentPage === pageNum ? "page" : undefined}
        >
          {pageNum}
        </button>
      );
    };

    // Всегда показываем первую страницу
    addPageNumber(1);

    if (totalPages <= 7) {
      // Если страниц мало, показываем все
      for (let i = 2; i < totalPages; i++) {
        addPageNumber(i);
      }
    } else {
      // Логика для отображения страниц с эллипсисом
      if (currentPage >= 4) {
        addEllipsis("start");
      }

      // Показываем страницы вокруг текущей
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      ) {
        // Всегда показываем предыдущую, текущую и следующую страницы
        if (i === currentPage - 1) {
          addPageNumber(i);
        } else if (i === currentPage) {
          addPageNumber(i);
        } else if (i === currentPage + 1) {
          addPageNumber(i);
        }
      }

      if (currentPage < totalPages - 3) {
        addEllipsis("end");
      }
    }

    // Всегда показываем последнюю страницу
    if (totalPages > 1) {
      addPageNumber(totalPages);
    }

    return pages;
  };

  return (
    <div
      className={styles.pagination}
      role="navigation"
      aria-label="Pagination"
    >
      <button
        className={styles.pageButton}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        ←
      </button>
      {renderPageNumbers()}
      <button
        className={styles.pageButton}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next page"
      >
        →
      </button>
    </div>
  );
};
